/*
 * <product-inline-video> — a product gallery video that plays on its own.
 *
 * The markup carries autoplay + muted + loop + playsinline, which is what lets
 * browsers (phones included) start it without a tap or a full-screen takeover.
 * This element covers what the attributes alone don't: starting it again when it
 * is moved in the DOM or scrolled into view, undoing the global pauseAllMedia(),
 * and a pause button, so a looping video never has to be put up with.
 */
if (!customElements.get('product-inline-video')) {
  customElements.define(
    'product-inline-video',
    class ProductInlineVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        // Embedded (YouTube / Vimeo) players are autoplayed by their own URL.
        if (!this.video) return;

        this.toggle = this.querySelector('[data-video-toggle]');
        this.userPaused = false;

        // The muted *attribute* doesn't always set the property, and an unmuted
        // video is not allowed to autoplay.
        this.video.muted = true;
        this.video.loop = true;
        this.video.controls = false;

        this.onPlayState = () => this.renderToggle();
        this.video.addEventListener('play', this.onPlayState);
        this.video.addEventListener('pause', this.onPlayState);
        this.onToggle = () => {
          this.userPaused = !this.video.paused;
          if (this.userPaused) this.video.pause();
          else this.resume(true);
        };
        this.toggle?.addEventListener('click', this.onToggle);

        // People who ask for less motion get the poster and a play button.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.userPaused = true;
          this.video.removeAttribute('autoplay');
          this.video.pause();
        }

        // A video scrolled or swiped into view that isn't running gets started:
        // the browser may have paused it while it was off screen.
        this.observer = new IntersectionObserver(
          (entries) => entries.forEach((entry) => entry.isIntersecting && this.resume()),
          { rootMargin: '200px' }
        );
        this.observer.observe(this);

        // iOS Low Power Mode refuses autoplay until the first touch.
        this.onFirstTouch = () => this.resume();
        window.addEventListener('pointerdown', this.onFirstTouch, { once: true, passive: true });

        this.renderToggle();
        this.resume();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        window.removeEventListener('pointerdown', this.onFirstTouch);
        this.video?.removeEventListener('play', this.onPlayState);
        this.video?.removeEventListener('pause', this.onPlayState);
        this.toggle?.removeEventListener('click', this.onToggle);
      }

      // force: the visitor asked for it, so a reduced-motion preference doesn't veto it.
      resume(force = false) {
        if (!this.video || (this.userPaused && !force)) return;
        if (!this.video.paused) return;
        const attempt = this.video.play();
        if (attempt) attempt.catch(() => {});
      }

      renderToggle() {
        if (!this.toggle) return;
        const playing = !this.video.paused;
        this.toggle.setAttribute('aria-pressed', String(!playing));
        this.toggle.querySelector('[data-icon-pause]').hidden = !playing;
        this.toggle.querySelector('[data-label-pause]').hidden = !playing;
        this.toggle.querySelector('[data-icon-play]').hidden = playing;
        this.toggle.querySelector('[data-label-play]').hidden = playing;
      }
    }
  );
}
